import React, {CSSProperties, MouseEvent, useEffect, useRef, useState} from "react";
import {useAppDispatch, useAppSelector} from "../../../Config/Hooks";
import {RootState} from "../../../Config/Store";
import {getEntities} from "./DashboardOverviewMapPageReducer";
import {getEntitiesWithoutParams, Performance} from "../PerformanceAnalysis/PerformanceAnalysisReducer";
import LeafletMap from "./types/LeafletMap";
import {Grid, Link, Paper, useTheme} from "@mui/material";
import Box from "@mui/material/Box";
import {formatData, FormattedData} from "./types/FormattedData";
import {getIconColor} from "../../../Shared/Components/Layout/menu/helpers/getIconColor";
import {faLocationDot} from "@fortawesome/free-solid-svg-icons";
import './stylesMap.css'
import {MenuItemStatus} from "../../../Shared/Components/Layout/menu/types/MenuItemStatus";
import {usePowerStationNavigationToPlant} from "../../../Shared/Hooks/usePowerStationNavigationToPlant";
import CloseIcon from "@mui/icons-material/Close";
import {usePowerStationNavigationToAlarmsPage} from "../../../Shared/Hooks/usePowerStationNavigationToAlarmsPage";
import useInterval from "../../../Shared/Hooks/useInterval";
import {getStatusName} from "../../../Shared/Components/Layout/menu/helpers/getStatusName";
import L from "leaflet";
import { Marker as RLMarker, Popup } from "react-leaflet";
import { icon } from "@fortawesome/fontawesome-svg-core";
import {PerformanceMapResponse} from "../../../Config/Types";


export interface MarkerPowerStation extends Performance {
    powerStationId: number;
    powerStationName: string;
    status: MenuItemStatus;
    powerCapacityNominal: number;
    generatedEnergySum: number;
    availability: number;
    performanceRatio: number;
    instantPower: number;
}

export interface MarkerPlace {
    id: number;
    name: string;
    latitude: number;
    longitude: number;
    status: MenuItemStatus;
    alarmMostRecently: string;
}

const InfoWindow = ({ place, entities, onClose }: { place: MarkerPlace, entities: MarkerPowerStation, onClose: (event: React.MouseEvent) => void  }) => {

    const theme = useTheme();
    const navigationToPowerStationAlarm = usePowerStationNavigationToAlarmsPage();
    const navigationToPlantByPowerStationId = usePowerStationNavigationToPlant();
    const handleNavigationPowerPlant = (event: MouseEvent<HTMLElement>, powerPlantId: number): void => {
        event.preventDefault();
        navigationToPowerStationAlarm(powerPlantId);
    };

    const handleNavigationToPlant = (event: MouseEvent<HTMLElement>, powerPlantId: number): void => {
        event.preventDefault();
        navigationToPlantByPowerStationId(powerPlantId);
    };

    useEffect(() => {
        document.documentElement.style.setProperty("--background-color", theme.palette.background.default);
    }, [theme]);

    const closeButtonStyle: CSSProperties = {
        position: "absolute",
        top: "-6px",
        right: "-6px",
        cursor: "pointer",
        padding: "6px",
        color: theme.palette.mode === 'light' ? 'black' : '#fff'
    };

    const imageStyle: CSSProperties = {
        width: "100%",
        height: "auto",
        objectFit: "cover",
        border: "1px solid black",
    };

    const contentStyle: CSSProperties = {
        backgroundColor: theme.palette.mode === 'light' ? '' : '#1E1E1E',
        textAlign: "left",
        padding: "10px",
    };

    const formattedData: FormattedData = formatData(entities);

    return (
        <div aria-label={'overview-map-pin'} className="containerStyle">
            <img
                src="https://img.olhardigital.com.br/wp-content/uploads/2017/03/20170320124419.jpg"
                alt="Imagem da Usina"
                style={imageStyle}
            />
            <div>
                <CloseIcon
                    style={closeButtonStyle}
                    onClick={onClose}
                />
            </div>
            <div aria-label={'overview-map-pin-content'} style={contentStyle}>
                <h1>{place.name}</h1>
                <p>Status: {getStatusName(entities?.status)}</p>
                <p>Último Alarme: {place?.alarmMostRecently ? place?.alarmMostRecently : "Não possui nenhum"}</p>
                <p>Capacidade Instalada: {formattedData?.powerCapacityNominal}</p>
                <p>Potência Instantânea: {formattedData?.instantPower}</p>
                <p>Energia Gerada: {formattedData?.generatedEnergy}</p>
                <p>Performance Ratio: {formattedData?.performanceRatio}</p>
                <p>Disponibilidade: {formattedData?.availability}</p>
                <Link href="#" onClick={(event) => handleNavigationPowerPlant(event, place?.id)} underline="hover">Tabela
                    de Alarmes</Link>
                <br/>
                <br/>
                <Link href="#" onClick={(event) => handleNavigationToPlant(event, place?.id)}
                      underline="hover">Unifilar</Link>
            </div>
        </div>
    );
};

/** ----- Marker custom com FontAwesome, usando L.divIcon ----- */
const Marker = ({
                    place,
                    entities,
                    isOpen,
                    onMarkerClick,
                    openMarkerId
                }: {
    place: MarkerPlace;
    entities: MarkerPowerStation;
    isOpen: boolean;
    onMarkerClick: () => void;
    openMarkerId: any
}) => {
    const iconColor = getIconColor(place?.status);



    const svgMarkup = icon(faLocationDot)?.html[0]; // gera <svg> em string

    // Cria um DivIcon que renderiza o FontAwesome no mapa
    const divIcon = L?.divIcon({
        className: "custom-fa-marker",
        html: `<div style="font-size:40px;color:${iconColor};
         filter:drop-shadow(2px 2px 4px rgba(0,0,0,0.7));cursor:pointer;">
           ${svgMarkup}
         </div>`,
        iconAnchor: [13, 40],
    });

    // 2) Refs do Leaflet para ESTE marker/popup
    const markerRef = useRef<L.Marker | null>(null);

    // 3) Sincroniza estado React -> Leaflet (abre/fecha só ESTE popup)
    useEffect(() => {
        const m = markerRef?.current;
        if (!m) return;
        if (isOpen) m?.openPopup();
        else m?.closePopup();
    }, [isOpen]);

    const handleCloseInfoWindow = (e: React.MouseEvent) => {
        e?.stopPropagation();
        markerRef?.current?.closePopup(); // fecha no Leaflet
        onMarkerClick();                 // atualiza openMarkerId no pai
    };

    return (
        <RLMarker
            ref={(inst: any) => (markerRef.current = inst ?? null)} // v4: inst é L.Marker
            position={[place?.latitude, place?.longitude]}
            icon={divIcon}
            eventHandlers={{ click: () => onMarkerClick() }}        // seta openMarkerId = place.id
        >
            {/* Popup SEM condicional (sempre montado) — quem controla abrir/fechar é o isOpen */}
            <Popup autoPan
                   keepInView
                   closeButton={false}
                   eventHandlers={{ remove: () => isOpen && onMarkerClick() }}
                   className="popup--bare"
            >

                <InfoWindow place={place} entities={entities} onClose={handleCloseInfoWindow} />
            </Popup>
        </RLMarker>
    );
};

const DashboardOverViewMapPage: React.FC = () => {
    const [places, setPlaces] = useState<any[]>([]);
    const informationsMapPowerStations = useAppSelector(
        (state: RootState) => state?.dashboardMapPage
    );
    const [openMarkerId, setOpenMarkerId] = useState<number | null>(null);
    const entities: PerformanceMapResponse = useAppSelector((state: RootState) => state?.performanceAnalysis?.entities) as unknown as PerformanceMapResponse;

    const userConfig = useAppSelector((state: RootState) => state?.userConfigurarion);
    const dispatch = useAppDispatch();

    useEffect(() => {
        dispatch(getEntities());
        dispatch(getEntitiesWithoutParams());
    }, [dispatch]);

    useInterval(() => {
        dispatch(getEntities());
        dispatch(getEntitiesWithoutParams());
    }, userConfig?.mainUpdateInterval); //5 minutes




    useEffect(() => {
        const placesData = informationsMapPowerStations?.entities?.map((powerStation) => {
            return {
                id: powerStation?.powerStationId,
                name: powerStation?.powerStationName,
                latitude: powerStation?.latitude,
                longitude: powerStation?.longitude,
                status: powerStation?.etsStatus,
                alarmMostRecently: powerStation?.alarmMostRecently,
            };
        });
        setPlaces(placesData);
    }, [informationsMapPowerStations]);


    const mapCenter = {lat: -18.5779679, lng: -45.4514553};


    const handleMarkerClick = (markerId: number) => {
        setOpenMarkerId(openMarkerId === markerId ? null : markerId);
    };


    const mapZoom = 5;


    return (
        <Paper elevation={3}>
            <Grid aria-label={"overview-map"} container spacing={2}>
                <Grid item xs={12}></Grid>
                <Grid item xs={12}>
                    <Box style={{ height: "1200px", width: "100%" }}>
                        <LeafletMap defaultZoom={mapZoom} defaultCenter={mapCenter}>
                            {places?.map((marker) => {
                                const entity = entities?.data?.find((item) => item?.powerStationId === marker?.id) as MarkerPowerStation | undefined;
                                if (!entity) return null;
                                return (
                                    <Marker
                                        openMarkerId={openMarkerId}
                                        key={marker?.id}
                                        place={marker}
                                        entities={entity}
                                        isOpen={openMarkerId === marker?.id}
                                        onMarkerClick={() => handleMarkerClick(marker?.id)}
                                    />
                                );
                            })}
                        </LeafletMap>
                    </Box>
                </Grid>
            </Grid>
        </Paper>
    );
};

export default DashboardOverViewMapPage;